// src/chat/promptStore.ts

export type PromptVersion = {
  id: string;
  name: string;
  systemPrompt: string;
  welcomeMessage: string;
  createdAt: string; // ISO
  updatedAt?: string; // ISO
};

const LS_VERSIONS = "dttrail_prompt_versions_v1";
const LS_ACTIVE_ID = "dttrail_prompt_active_id_v1";

export const DEFAULT_WELCOME_MESSAGE =
  "Welkom bij Design Thinking Trail! Vandaag wacht er een mysterie op ons… Klaar voor?";

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function nowIso() {
  return new Date().toISOString();
}

function makeId() {
  return `v_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

function readVersions(): PromptVersion[] {
  return safeParse<PromptVersion[]>(localStorage.getItem(LS_VERSIONS), []);
}

function writeVersions(versions: PromptVersion[]) {
  localStorage.setItem(LS_VERSIONS, JSON.stringify(versions));
}

function readActiveId(): string | null {
  return localStorage.getItem(LS_ACTIVE_ID);
}

function writeActiveId(id: string) {
  localStorage.setItem(LS_ACTIVE_ID, id);
}

export function ensureSeeded(defaultPrompt: string) {
  const versions = readVersions();
  const activeId = readActiveId();

  // ✅ MIGRATION: fill missing fields in existing versions (older localStorage)
  if (versions.length > 0) {
    let changed = false;

    const migrated = versions.map((v: any) => {
      const next: any = { ...v };

      // legacy: old field name
      if (typeof next.systemPrompt !== "string" && typeof next.content === "string") {
        next.systemPrompt = next.content;
        delete next.content;
        changed = true;
      }

      if (typeof next.systemPrompt !== "string") {
        next.systemPrompt = defaultPrompt;
        changed = true;
      }

      if (typeof next.welcomeMessage !== "string" || !next.welcomeMessage.trim()) {
        next.welcomeMessage = DEFAULT_WELCOME_MESSAGE;
        changed = true;
      }

      if (typeof next.createdAt !== "string") {
        next.createdAt = nowIso();
        changed = true;
      }

      return next as PromptVersion;
    });

    if (changed) writeVersions(migrated);

    // ensure activeId exists
    if (!activeId && migrated[0]) writeActiveId(migrated[0].id);

    return;
  }

  // ✅ Seed brand-new store with default
  const seeded: PromptVersion = {
    id: makeId(),
    name: "Bella — Default",
    systemPrompt: defaultPrompt,
    welcomeMessage: DEFAULT_WELCOME_MESSAGE,
    createdAt: nowIso(),
  };

  writeVersions([seeded]);
  writeActiveId(seeded.id);
}

export function listVersions(): PromptVersion[] {
  return readVersions();
}

export function getActiveVersion(): PromptVersion | null {
  const versions = readVersions();
  const activeId = readActiveId();
  if (!activeId) return versions[0] ?? null;
  return versions.find((v) => v.id === activeId) ?? versions[0] ?? null;
}

export function getActiveVersionOrDefault(defaultPrompt: string): PromptVersion {
  ensureSeeded(defaultPrompt);
  return getActiveVersion()!;
}

export function setActiveVersion(id: string) {
  const versions = readVersions();
  const exists = versions.some((v) => v.id === id);
  if (!exists) return;
  writeActiveId(id);
}

export function createVersion(
  name: string,
  systemPrompt: string,
  welcomeMessage: string
): PromptVersion {
  const versions = readVersions();

  const v: PromptVersion = {
    id: makeId(),
    name: (name || "Nieuwe versie").trim(),
    systemPrompt: systemPrompt ?? "",
    welcomeMessage: (welcomeMessage || DEFAULT_WELCOME_MESSAGE).trim(),
    createdAt: nowIso(),
  };

  const next = [v, ...versions];
  writeVersions(next);
  writeActiveId(v.id);
  return v;
}

export function updateVersion(
  id: string,
  patch: Partial<Pick<PromptVersion, "name" | "systemPrompt" | "welcomeMessage">>
) {
  const versions = readVersions();
  const next = versions.map((v) => {
    if (v.id !== id) return v;
    return {
      ...v,
      ...patch,
      updatedAt: nowIso(),
    };
  });
  writeVersions(next);
}

export function deleteVersion(id: string) {
  const versions = readVersions();
  const next = versions.filter((v) => v.id !== id);
  writeVersions(next);

  const activeId = readActiveId();
  if (activeId === id) {
    if (next[0]) writeActiveId(next[0].id);
    else localStorage.removeItem(LS_ACTIVE_ID);
  }
}

export function exportOneVersion(id: string): string | null {
  const versions = readVersions();
  const v = versions.find((x) => x.id === id);
  if (!v) return null;

  return JSON.stringify(
    {
      schema: "techtok.promptVersion.v1",
      version: v,
    },
    null,
    2
  );
}

export function exportAllVersions(): string {
  const versions = readVersions();
  const activeId = readActiveId();

  return JSON.stringify(
    {
      schema: "techtok.promptPack.v1",
      activeId: activeId ?? null,
      versions,
    },
    null,
    2
  );
}

export function importJson(raw: string): { added: number; updated: number } {
  const data = safeParse<any>(raw, null);
  if (!data) throw new Error("Ongeldige JSON");

  const existing = readVersions();
  const byId = new Map(existing.map((v) => [v.id, v] as const));

  let added = 0;
  let updated = 0;

  if (data?.schema === "techtok.promptVersion.v1" && data?.version) {
    const rawV: any = data.version;

    // Backward compatible: accept legacy `content` as systemPrompt
    const systemPrompt =
      typeof rawV.systemPrompt === "string"
        ? rawV.systemPrompt
        : typeof rawV.content === "string"
          ? rawV.content
          : null;

    if (!rawV?.id || typeof rawV.id !== "string" || typeof systemPrompt !== "string") {
      throw new Error("JSON mist version.id of systemPrompt (of legacy content)");
    }

    const v: PromptVersion = {
      id: rawV.id,
      name: typeof rawV.name === "string" ? rawV.name : "Geïmporteerde versie",
      systemPrompt,
      welcomeMessage:
        typeof rawV.welcomeMessage === "string" && rawV.welcomeMessage.trim()
          ? rawV.welcomeMessage
          : DEFAULT_WELCOME_MESSAGE,
      createdAt: typeof rawV.createdAt === "string" ? rawV.createdAt : nowIso(),
      updatedAt: typeof rawV.updatedAt === "string" ? rawV.updatedAt : undefined,
    };

    if (byId.has(v.id)) {
      byId.set(v.id, { ...byId.get(v.id)!, ...v, updatedAt: nowIso() });
      updated++;
    } else {
      byId.set(v.id, v);
      added++;
    }
  } else if (data?.schema === "techtok.promptPack.v1" && Array.isArray(data?.versions)) {
    for (const rawV of data.versions as any[]) {
      const systemPrompt =
        typeof rawV.systemPrompt === "string"
          ? rawV.systemPrompt
          : typeof rawV.content === "string"
            ? rawV.content
            : null;

      if (!rawV?.id || typeof rawV.id !== "string" || typeof systemPrompt !== "string") continue;

      const v: PromptVersion = {
        id: rawV.id,
        name: typeof rawV.name === "string" ? rawV.name : "Geïmporteerde versie",
        systemPrompt,
        welcomeMessage:
          typeof rawV.welcomeMessage === "string" && rawV.welcomeMessage.trim()
            ? rawV.welcomeMessage
            : DEFAULT_WELCOME_MESSAGE,
        createdAt: typeof rawV.createdAt === "string" ? rawV.createdAt : nowIso(),
        updatedAt: typeof rawV.updatedAt === "string" ? rawV.updatedAt : undefined,
      };

      if (byId.has(v.id)) {
        byId.set(v.id, { ...byId.get(v.id)!, ...v, updatedAt: nowIso() });
        updated++;
      } else {
        byId.set(v.id, v);
        added++;
      }
    }

    if (data.activeId && byId.has(data.activeId)) {
      writeActiveId(data.activeId);
    }
  } else {
    throw new Error("Onbekend prompt-bestand (schema mismatch)");
  }

  const merged = Array.from(byId.values()).sort((a, b) => {
    const ad = a.updatedAt || a.createdAt;
    const bd = b.updatedAt || b.createdAt;
    return bd.localeCompare(ad);
  });

  writeVersions(merged);

  const active = readActiveId();
  if (!active || !merged.some((v) => v.id === active)) {
    if (merged[0]) writeActiveId(merged[0].id);
  }

  return { added, updated };
}
